'use client'

import { useEffect, useState } from 'react'
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs'
import { useToast } from '@/hooks/use-toast'

export type NotificationType = 
  | "article" 
  | "event" 
  | "system" 
  | "company" 
  | "mention"
  | "follow";

export type Notification = {
  id: string
  user_id: string
  title: string
  content: string
  link: string
  read: boolean
  created_at: string
  updated_at: string
}

export type NotificationSettings = {
  id?: string
  user_id?: string
  articles_updates: boolean
  comments_replies: boolean
  account_notifications: boolean
  marketing_emails: boolean
  browser_notifications: boolean
  created_at?: string
  updated_at?: string
}

/**
 * Hook that provides access to the user's notifications
 */
export function useNotifications(limit?: number) {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [unreadCount, setUnreadCount] = useState(0)
  const { toast } = useToast()
  const supabase = createClientComponentClient()

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true)
      
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) {
        setNotifications([])
        setUnreadCount(0)
        return
      }

      // Get all notifications for the user, or limit them if specified
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
      
      if (limit) {
        query = query.limit(limit)
      }

      const { data, error } = await query
      
      if (error) throw error
      
      setNotifications(data as Notification[])
      
      // Count unread notifications
      const unread = data?.filter(n => !n.read).length || 0
      setUnreadCount(unread)
    } catch (error: any) {
      console.error('Error fetching notifications:', error)
      toast({
        title: 'Failed to load notifications',
        description: error.message,
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  // Mark notification as read
  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true, updated_at: new Date().toISOString() })
        .eq('id', id)
      
      if (error) throw error
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      )
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (error: any) {
      console.error('Error marking notification as read:', error)
      toast({
        title: 'Failed to update notification',
        description: error.message,
        variant: 'destructive',
      })
    }
  }

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) return
      
      const { error } = await supabase
        .from('notifications')
        .update({ read: true, updated_at: new Date().toISOString() })
        .eq('user_id', session.user.id)
        .eq('read', false)
      
      if (error) throw error
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      )
      
      // Reset unread count
      setUnreadCount(0)
    } catch (error: any) {
      console.error('Error marking all notifications as read:', error)
      toast({
        title: 'Failed to update notifications',
        description: error.message,
        variant: 'destructive',
      })
    }
  }

  // Delete notification
  const deleteNotification = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      
      // Update local state
      const deleted = notifications.find(n => n.id === id)
      setNotifications(prev => prev.filter(n => n.id !== id))
      
      // Update unread count if the deleted notification was unread
      if (deleted && !deleted.read) {
        setUnreadCount(prev => Math.max(0, prev - 1))
      }

      toast({
        title: 'Notification deleted',
        variant: 'default',
      })
    } catch (error: any) {
      console.error('Error deleting notification:', error)
      toast({
        title: 'Failed to delete notification',
        description: error.message,
        variant: 'destructive',
      })
    }
  }
  
  useEffect(() => {
    fetchNotifications()
    
    // Set up realtime subscription for notifications
    const channel = supabase
      .channel('notifications-changes')
      .on('postgres_changes', 
        {
          event: '*',
          schema: 'public',
          table: 'notifications'
        }, 
        (payload) => {
          fetchNotifications()
        }
      )
      .subscribe()
    
    return () => {
      supabase.removeChannel(channel)
    }
  }, [])

  return {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    fetchNotifications
  }
}

/**
 * Loads notification settings for the current user
 */
export async function loadNotificationSettings(supabase: any): Promise<NotificationSettings> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) {
    throw new Error('User not authenticated')
  }

  // Check if settings exist for the user
  const { data, error } = await supabase
    .from('notification_settings')
    .select('*')
    .eq('user_id', session.user.id)
    .single()

  // If no rows found (PGRST116), create default settings
  if (error && error.code === 'PGRST116') {
    // Default notification settings
    const defaultSettings: Partial<NotificationSettings> = {
      articles_updates: true,
      comments_replies: true,
      account_notifications: true,
      marketing_emails: false,
      browser_notifications: false
    }
    
    return createNotificationSettings(supabase, defaultSettings)
  }
  
  if (error) {
    console.error('Error loading notification settings:', error)
    throw error
  }

  return data as NotificationSettings
}

/**
 * Creates notification settings for the current user
 */
export async function createNotificationSettings(
  supabase: any, 
  settings: Partial<Omit<NotificationSettings, "id" | "user_id" | "created_at" | "updated_at">>
): Promise<NotificationSettings> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) {
    throw new Error('User not authenticated')
  }

  const newSettings = {
    user_id: session.user.id,
    articles_updates: settings.articles_updates ?? true,
    comments_replies: settings.comments_replies ?? true,
    account_notifications: settings.account_notifications ?? true,
    marketing_emails: settings.marketing_emails ?? false,
    browser_notifications: settings.browser_notifications ?? false
  }

  const { data, error } = await supabase
    .from('notification_settings')
    .insert(newSettings)
    .select()
    .single()

  if (error) {
    console.error('Error creating notification settings:', error)
    throw error
  }

  return data as NotificationSettings
}

/**
 * Updates notification settings for the current user
 */
export async function updateNotificationSettings(
  supabase: any, 
  settings: Partial<Omit<NotificationSettings, "id" | "user_id" | "created_at" | "updated_at">>
): Promise<NotificationSettings> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.user) {
    throw new Error('User not authenticated')
  }

  // First try to load the current settings
  try {
    await loadNotificationSettings(supabase)
  } catch (error: any) {
    // If settings don't exist, create them first
    if (error.code === 'PGRST116') {
      return createNotificationSettings(supabase, settings)
    }
    throw error
  }

  // Update the settings
  const { data, error } = await supabase
    .from('notification_settings')
    .update(settings)
    .eq('user_id', session.user.id)
    .select()
    .single()

  if (error) {
    console.error('Error updating notification settings:', error)
    throw error
  }

  return data as NotificationSettings
}

/**
 * Creates a notification for a user
 */
export async function createNotification(
  supabase: any,
  userId: string,
  title: string,
  content: string,
  link: string = '/dashboard/notifications'
): Promise<Notification> {
  const newNotification = {
    user_id: userId,
    title,
    content,
    link,
    read: false
  }

  const { data, error } = await supabase
    .from('notifications')
    .insert(newNotification)
    .select()
    .single()

  if (error) {
    console.error('Error creating notification:', error)
    throw error
  }

  return data as Notification
} 